import { Injectable } from '@angular/core';
import {
  AutomobileRiskModels,
  brokerRoutes,
  QuotationModels,
} from '@wz-models';
import { isStringNullOrEmpty } from '@wz-utilities/string.utilities';
import { BaseNavigationService } from './base-navigation.service';
import { ILogger } from './logger/ilogger';
import { LoggerService } from './logger/logger.service';
import { SessionStorageService } from './storage/session-storage.service';

const STEPS_DEFINITION: { [key: number]: string } = {
  0: brokerRoutes.APP_CAR_INSURANCE_QUOTATION,
  1: brokerRoutes.APP_CAR_INSURANCE_PRICES,
  2: brokerRoutes.APP_CAR_INSURANCE_SUBSCRIPTION,
  3: brokerRoutes.APP_CAR_INSURANCE_PAYMENT,
  4: brokerRoutes.APP_CAR_INSURANCE_CONFIRMATION,
};

export const CAR_RISK_STORAGE_KEY = 'CAR_RISK';
export const CAR_QUOTATION_DETAILS_KEY = 'CAR_QUOTATION_DETAILS';

@Injectable({
  providedIn: 'root',
})
export class CarInsuranceService {
  private logger: ILogger;

  public maxStep = 0;
  public currentStep = 0;

  public isStepperDisabled!: boolean;
  public quotationId!: string | null;

  private _riskFormData!: AutomobileRiskModels.Form | null;
  private _quotationDetails: QuotationModels.QuotationDetails | null = null;

  public hasSubmittedOnce = false;

  constructor(
    private navService: BaseNavigationService,
    logger: LoggerService,
    private sessionStorageService: SessionStorageService,
  ) {
    this.logger = logger.getLogger('CAR INSURANCE');
  }

  get quotationDetails(): QuotationModels.QuotationDetails | null {
    if (this._quotationDetails) {
      return this._quotationDetails;
    }

    const fromStorage =
      this.sessionStorageService.getObject<QuotationModels.QuotationDetails>(
        CAR_QUOTATION_DETAILS_KEY,
      );
    this._quotationDetails = fromStorage;
    return fromStorage;
  }

  set quotationDetails(details: QuotationModels.QuotationDetails | null) {
    this._quotationDetails = details;
    if (details) {
      this.sessionStorageService.setObject(CAR_QUOTATION_DETAILS_KEY, details);
    } else {
      this.sessionStorageService.clearItem(CAR_QUOTATION_DETAILS_KEY);
    }
  }

  get riskFormData(): AutomobileRiskModels.Form {
    if (this._riskFormData) {
      return this._riskFormData;
    }

    const fromStorage =
      this.sessionStorageService.getObject<AutomobileRiskModels.Form>(
        CAR_RISK_STORAGE_KEY,
      );
    this._riskFormData = fromStorage;
    return fromStorage;
  }

  set riskFormData(riskForm: AutomobileRiskModels.Form) {
    this._riskFormData = riskForm;
    if (riskForm) {
      this.sessionStorageService.setObject(CAR_RISK_STORAGE_KEY, riskForm);
    } else {
      this.sessionStorageService.clearItem(CAR_RISK_STORAGE_KEY);
    }
  }

  goPricingFromQuotation(risk: AutomobileRiskModels.Form) {
    // set risk
    this.riskFormData = risk;
    // clear any quotation details
    this.quotationDetails = null;
    this.goToStep(1);
  }

  updateStepper(step: number) {
    this.maxStep = step;
    this.currentStep = step;
  }

  enableStepper() {
    this.isStepperDisabled = false;
  }

  disableStepper() {
    this.isStepperDisabled = true;
  }

  goToStep(step: number) {
    if (STEPS_DEFINITION[step]) {
      const avoidNavigation = step === this.currentStep;
      this.updateStepper(step);
      if (avoidNavigation) {
        return;
      }
      if (!isStringNullOrEmpty(this.quotationId!)) {
        this.navService.navigate([
          STEPS_DEFINITION[step].format(this.quotationId),
        ]);
      } else {
        this.navService.navigate([STEPS_DEFINITION[step]]);
      }
    } else {
      this.logger.error('The step did not have any stepDefinition', {
        step,
        STEPS_DEFINITION,
      });
    }
  }

  clear(): any {
    this.hasSubmittedOnce = false;
    this._riskFormData = null;
    this._quotationDetails = null;
    this.quotationId = null;
    this.currentStep = 0;
    this.maxStep = 0;
    this.isStepperDisabled = false;
  }
}
